import {
    createProductCard,
    IProductCardItemProperties,
    IProductCardProperties,
    ProductCard,
} from '@vfde-brix/ws10/product-card';
import { StateProps } from '../container/App/interfaces/state';
import {
    NO_PATTERN_BUSINESS_LOGIC,
    Price,
} from '@vfde-brix/ws10/core';
import {
    AbstractedHardwareOverviewResponse,
    PriceType,
} from '@vfde-sails/glados-v2';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import { PromoPriceVariant } from '@vfde-brix/ws10/promo-price';
import {
    SYSTEM_ICON_4G,
    SYSTEM_ICON_5G,
    SYSTEM_ICON_5G_PLUS,
} from '@vfde-brix/ws10/system-icon';
import { selectProduct } from '../container/App/slice';
import { startAppListening } from '../container/App/listener';
import {
    mapStateToProps,
    selectDevicesByCategory,
} from '../container/App/selectors';
import { useDispatch } from '../store';

/**
 * Product card
 */
export default function mountProductCard (
    id: string,
    properties: IProductCardProperties,
): ProductCard | null {
    const dispatch = useDispatch();
    const container = document.getElementById(id);

    /* istanbul ignore if */
    if (!container) {
        return null;
    }

    const productCard = createProductCard(container, {
        ...properties,
        business: {
            onClick: (event, productId) => {
                event.preventDefault();
                dispatch(selectProduct(`${productId}`));
            },
        },
    });

    startAppListening({
        predicate: (action, currentState, previousState) =>
            selectDevicesByCategory(currentState) !== selectDevicesByCategory(previousState),
        effect: (action, listenerApi) => {
            const state = mapStateToProps(listenerApi.getState());

            const items = convertApiProductsToProductCardItems(state);

            productCard?.update({
                productCardItems: items,
            });
        },
    });

    return productCard;
}

/**
 * Convert products from API to feature card item properties
 */
export const convertApiProductsToProductCardItems = (state: StateProps): IProductCardItemProperties[] => {
    const { devices: items } = state;
    const {
        hardwareSublevelText,
        devices: { item: { priceConditionAfter, priceConditionBefore, moreInfoElement } },
    } = window[ADDITIONAL_PAGE_OPTIONS];
    const badgesConfig = window[ADDITIONAL_PAGE_OPTIONS].deviceCardBadges;

    return items!.map((item: AbstractedHardwareOverviewResponse['devices'][number]): IProductCardItemProperties => {
        const flagBadge = badgesConfig?.[item.modelId!]?.flagBadge;
        const highlightBadge = badgesConfig?.[item.modelId!]?.highlightBadge;
        const stdCornerIcon = getProductCardIcon(item.connectivity!);

        const containerPromoPrices: IProductCardItemProperties['containerPromoPrices'] = [];

        if (item.priceHardwareOneTimeWithTariff) {
            containerPromoPrices.push({
                price: Price.fromNumber(item.priceHardwareOneTimeWithTariff.gross),
                stdConditionAfter: priceConditionAfter,
                optPriceConditionBefore: state.paymentMethod !== PriceType.Monthly ? priceConditionBefore : undefined,
                optSizeVariation: PromoPriceVariant.Medium,
                business: NO_PATTERN_BUSINESS_LOGIC,
            });
        }

        return {
            linkProductUrl: item.hubpageUrl,
            stdMoreInfoElement: moreInfoElement,
            stdProductId: parseInt(item.defaultAtomicId, 10),
            stdHeadline: item.modelName,
            imgProductCard: item.galleryImageUrl,
            containerColorSwatch: {
                items: item.colors?.map(color => ({
                    stdColorRgbValue: color.primaryColorRgb,
                })) || [],
                business: NO_PATTERN_BUSINESS_LOGIC,
            },
            containerPromoPrices,
            ...state.paymentMethod === PriceType.Monthly && { stdDescription: `+ ${item.priceSubLevel.gross.toLocaleString('de')} € ${hardwareSublevelText}` },
            ...stdCornerIcon && { stdCornerIcon },
            business: NO_PATTERN_BUSINESS_LOGIC,
            ...flagBadge && { containerFlagBadge: {
                stdLabel: flagBadge.stdLabel,
                optColor: flagBadge.optColor,
                optFullwidth: flagBadge.optFullWidth === 'true',
                business: NO_PATTERN_BUSINESS_LOGIC,
            } },
            ...highlightBadge && { containerHighlightBadge: {
                txtContent: highlightBadge.text,
                optColor: highlightBadge.color,
                optSize: highlightBadge.size,
                stdIcon: highlightBadge.icon,
                business: NO_PATTERN_BUSINESS_LOGIC,
            } },
            optWithBorder: flagBadge?.optWithBorder === 'true',
        };
    });
};

/**
 * Get Image Gallery Icon
 */
const getProductCardIcon = (cellular: string | null): string | null => {
    const cellularMap = {
        '4G': SYSTEM_ICON_4G,
        '5G': SYSTEM_ICON_5G,
        '5GPlus': SYSTEM_ICON_5G_PLUS,
    };

    return cellular && cellularMap[cellular as keyof typeof cellularMap];
};
